// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

/**
 * Settings List Component
 * 
 * RESPONSIBILITY: Display list of settings with options
 */

import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Button,
  List,
  ListItem,
  Chip,
  Grid,
  Box,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';

const SCALAR_VALUE_TYPES = ['string', 'number', 'object'];

function previewScalarValue(setting) {
  const v = setting.setting_value;
  if (v === null || v === undefined) return '(empty)';
  if (setting.value_type === 'object') {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v);
  if (s.length > 200) {
    return `${s.slice(0, 200)}…`;
  }
  return s || '(empty)';
}

const SettingsList = ({
  settings,
  onAddOption,
  onEditOption,
  onDeleteOption,
  onUpdateSetting,
  onEditScalarValue,
  canUpdateSettings = true,
  showVoiceProcessingRestore = false,
  onRestoreVoiceDefault,
}) => {
  if (!settings || settings.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 5 }}>
        <Typography color="text.secondary">No settings found in this category</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {settings.map((setting) => {
        const isBoolean = setting.value_type === 'boolean';
        const isArrayType = setting.value_type === 'array';
        const isScalar =
          !isBoolean &&
          !isArrayType &&
          SCALAR_VALUE_TYPES.includes(setting.value_type);
        const boolValue = isBoolean && (setting.setting_value === true || setting.setting_value === 'true' || setting.setting_value === 1);
        const showRestoreDefault =
          showVoiceProcessingRestore &&
          canUpdateSettings &&
          typeof onRestoreVoiceDefault === 'function' &&
          (isBoolean || isScalar);
        return (
        <Card key={setting.id} sx={{ mb: 3 }}>
          <CardHeader 
            title={setting.display_name}
            subheader={setting.description}
            action={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {setting.is_system && (
                  <Chip label="System" color="info" size="small" />
                )}
                {isArrayType && canUpdateSettings && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onAddOption(setting)}
                  >
                    + Add Option
                  </Button>
                )}
                {isScalar && onEditScalarValue && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => onEditScalarValue(setting)}
                    disabled={!canUpdateSettings}
                  >
                    Edit value
                  </Button>
                )}
                {showRestoreDefault && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => onRestoreVoiceDefault(setting)}
                    sx={{ borderColor: '#13213c', color: '#13213c' }}
                  >
                    Restore default
                  </Button>
                )}
              </Box>
            }
            sx={{ backgroundColor: '#f8f9fa' }}
          />
          <CardContent>
            {isBoolean ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!boolValue}
                    disabled={!canUpdateSettings}
                    onChange={async () => {
                      const newValue = !boolValue;
                      if (onUpdateSetting) {
                        await onUpdateSetting(setting.id, { setting_value: newValue });
                      }
                    }}
                    color="primary"
                  />
                }
                label={boolValue ? 'On' : 'Off'}
              />
            ) : isScalar ? (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Value type: {setting.value_type}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'monospace',
                    bgcolor: 'grey.50',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {previewScalarValue(setting)}
                </Typography>
              </Box>
            ) : setting.value_type === 'array' && Array.isArray(setting.setting_value) ? (
              <List>
                {setting.setting_value
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map((option, index) => {
                    const isGlossary = setting.category === 'translation_glossary';
                    return (
                    <ListItem 
                      key={option.value || option.label || index}
                      sx={{ 
                        borderBottom: '1px solid #eee',
                        '&:last-child': { borderBottom: 'none' }
                      }}
                    >
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={isGlossary ? 8 : 3}>
                          {isGlossary ? (
                            <Typography variant="subtitle1" fontWeight="bold">
                              {option.value || '(empty)'} → {option.label || '(empty)'}
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {option.label}
                              </Typography>
                              <Chip label={option.value} size="small" variant="outlined" />
                            </>
                          )}
                        </Grid>
                        <Grid item xs={12} md={isGlossary ? 2 : 7}>
                          {!isGlossary && (
                            <Typography variant="body2" color="text.secondary">
                              {option.description}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                          <Button
                            size="small"
                            onClick={() => onEditOption(setting, option)}
                            disabled={!canUpdateSettings}
                          >
                            Edit
                          </Button>
                          {!setting.is_system && (
                            <Button
                              size="small"
                              color="error"
                              onClick={() => onDeleteOption(setting, option.value)}
                              disabled={!canUpdateSettings}
                            >
                              Delete
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    </ListItem>
                    );
                  })}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                <em>Value type: {setting.value_type}</em>
              </Typography>
            )}
          </CardContent>
        </Card>
        );
      })}
    </Box>
  );
};

export default SettingsList;

